import { useCallback, useMemo, useState } from 'react';

import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';

import { US } from '../../../constants/country-codes';
import { useYupFormValidation } from '../../../validation/use-yup-form-validation';
import type { ValidationError } from '../../../validation/validation-error';
import { messages as rootMessages } from '../messages';
import type { WwexFormData } from '../types';
import { buildAccountInfoSchema } from '../wwex-form-validation-schema';

import { UserInfoForm } from './user-info-form';
import { WwexClearAllLink } from './wwex-clear-all-link';
import { messages } from './edit-account-info-view.messages';
import { WwexSubViewFooter } from './wwex-sub-view-footer';
import { WwexSubViewHeader } from './wwex-sub-view-header';
import style from './edit-account-info-view.module.less';
import modalStyle from '../wwex-registration-modal.module.less';

const EMPTY_ACCOUNT_INFO: Pick<WwexFormData, 'contactFullName' | 'companyName' | 'email' | 'phoneNumber'> = {
  contactFullName: '',
  companyName: '',
  email: '',
  phoneNumber: '',
};

type EditAccountInfoViewProps = {
  headerId: string;
  formData: Partial<WwexFormData>;
  isDisabled: boolean;
  sellerCountry?: string;
  onSave: (data: Partial<WwexFormData>) => void;
  onBack: () => void;
  onCancel: () => void;
};

export const EditAccountInfoView = ({
  headerId,
  formData,
  isDisabled,
  sellerCountry,
  onSave,
  onBack,
  onCancel,
}: EditAccountInfoViewProps) => {
  const [draft, setDraft] = useState({
    contactFullName: formData.contactFullName ?? '',
    companyName: formData.companyName ?? '',
    email: formData.email ?? '',
    phoneNumber: formData.phoneNumber ?? '',
  });
  const [showAllErrors, setShowAllErrors] = useState(false);

  const schema = useMemo(() => buildAccountInfoSchema(sellerCountry ?? US), [sellerCountry]);
  const { errors, isValid } = useYupFormValidation({ form: draft, schema });

  const handleFieldChange = useCallback((field: string, value: string) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleClearAll = useCallback(() => setDraft(EMPTY_ACCOUNT_INFO), []);

  const handleSave = useCallback(() => {
    if (!isValid) {
      setShowAllErrors(true);
      return;
    }

    onSave({ ...formData, ...draft });
  }, [draft, formData, isValid, onSave]);

  return (
    <>
      <ModalHeader className={modalStyle.modalHeader} closeModal={onCancel} id={headerId}>
        <WwexSubViewHeader isDisabled={isDisabled} onBack={onBack} titleMessage={rootMessages.editAccountInfoTitle} />
      </ModalHeader>

      <ModalBody className={modalStyle.modalBody}>
        <FlexColumn gap={Gap.Space16}>
          <FlexRow className={style.sectionHeaderRow} gap={Gap.Space8}>
            <Text textType={TextType.H4} value={messages.sectionTitle} />
            <WwexClearAllLink isDisabled={isDisabled} onClick={handleClearAll} />
          </FlexRow>

          <UserInfoForm
            companyName={draft.companyName}
            contactFullName={draft.contactFullName}
            email={draft.email}
            errors={errors as Record<string, ValidationError>}
            isDisabled={isDisabled}
            onBlur={noop}
            onChange={handleFieldChange}
            phoneNumber={draft.phoneNumber}
            showAllErrors={showAllErrors}
          />
        </FlexColumn>
      </ModalBody>

      <ModalFooter>
        <WwexSubViewFooter isDisabled={isDisabled} isSaveDisabled={!isValid} onBack={onBack} onCancel={onCancel} onSave={handleSave} />
      </ModalFooter>
    </>
  );
};
